import i18n from '../i18n.js';
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- bracket access on import.meta.env returns `any`; value is string|undefined from Vite env
const BASE = import.meta.env['VITE_API_BASE'] ?? '';

// El idioma en que se navega viaja con cada llamada: el servidor escribe los
// hallazgos, los motivos y los avisos en ese idioma (server/services/idioma.ts).
function idioma(): string {
  return (i18n.language || 'es').slice(0, 2);
}

// Bug real (2026-08-05): antes, cualquier fallo (401 real, 500 transitorio,
// caída de red) se convertía en el mismo `Error` genérico — el caller no
// podía distinguir "tu sesión venció" de "esta request puntual falló".
// ApiError.status permite esa distinción; status:0 marca un fallo de red
// (fetch() nunca llegó a tener respuesta — offline, DNS, CORS) para que un
// caller pueda tratarlo igual que un 5xx (reintentable) y nunca como un 401.
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** El código de máquina (`err.error` del body) cuando el servidor manda uno
     *  — 'windowClosed', 'siteUnreachable', 'rateLimited'. Vive acá y NO en
     *  `message` para que quien necesite ramificar por caso tenga dónde
     *  mirar sin robarle el lugar al texto que lee una persona. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Saca el texto para mostrar del body de un error.
 *
 * `message` PRIMERO y `error` como respaldo. Al revés estuvo hasta el
 * 2026-08-25 y el efecto fue el peor posible: ocho rutas mandaban un código de
 * máquina en `error` y la explicación en castellano en `message`, y la
 * explicación no se mostró nunca. Un caso real — el guion del negocio devolvía
 * 422 con "No se pudo leer <url>: la dirección no es válida" — se leyó en
 * pantalla como "siteUnreachable", y de ahí se concluyó que el sitio estaba
 * caído cuando respondía perfecto. Un error correcto se leyó como una falla
 * inexistente porque el texto correcto nunca salió del body.
 */
function readableError(body: { error?: string; message?: string }, status: number): string {
  return body.message ?? body.error ?? `HTTP ${status}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Evo-Lang': idioma() },
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : null,
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0);
  }
  if (!res.ok) {
    // 304 NO es un error: es "no cambió nada desde la última vez".
    //
    // `res.ok` es false para 304 y por eso caía acá, con el body vacío que un
    // 304 tiene por definición: se lanzaba `ApiError("HTTP 304")` y la pantalla
    // se quedaba sin datos. Medido el 2026-09-04 en el botón "Revisar ahora"
    // del análisis SEO: el servidor contestaba 304 dos veces seguidas y el
    // panel no mostraba ni resultado ni error.
    //
    // No hay body que devolver, así que se relanza como un error propio que
    // quien llama puede distinguir. react-query conserva el dato anterior, que
    // es exactamente el correcto cuando nada cambió.
    if (res.status === 304) throw new ApiError('not_modified', 304, 'not_modified');
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(readableError(err, res.status), res.status, err.error);
  }
  return res.json() as Promise<T>;
}

/** ¿Este error es solo "no cambió nada"? Ver el 304 en request(). */
export function esSinCambios(e: unknown): boolean {
  return e instanceof ApiError && e.status === 304;
}

async function upload<T>(path: string, formData: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Evo-Lang': idioma() },
      credentials: 'include',
      body: formData,
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0);
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(readableError(err, res.status), res.status, err.error);
  }
  return res.json() as Promise<T>;
}

// Rutas que devuelven texto plano (hoy: el markdown del plan de contenido).
// Mismo manejo de error que request(): el body de error sigue siendo JSON.
async function requestText(path: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Evo-Lang': idioma() },
      credentials: 'include',
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0);
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(readableError(err, res.status), res.status, err.error);
  }
  return res.text();
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  getText: (path: string) => requestText(path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  upload: <T>(path: string, formData: FormData) => upload<T>(path, formData),
};
